import React, { useCallback, useEffect, useState } from 'react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskySpinner,
  TaskySwitch,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  useConfirm,
} from '../tasky';
import tutorialService, { type AdminTutorialSetting } from '../../services/api/tutorialService';
import { TUTORIAL_PAGES } from '../tutorial/registry';

/**
 * 管理画面「チュートリアル」。全体とページ別の表示ON/OFFを切り替え、既読をリセットする。
 * スイッチは押した時点で保存する（利用者側には最大60秒で反映）。
 */
const TutorialSettingsPanel: React.FC = () => {
  const confirm = useConfirm();
  const [data, setData] = useState<AdminTutorialSetting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await tutorialService.getAdminSetting());
    } catch {
      setError('チュートリアル設定を読み込めませんでした。');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (payload: { enabled?: boolean; disabled_pages?: string[] }, doneMessage: string) => {
      setIsSaving(true);
      setMessage(null);
      setError(null);
      try {
        setData(await tutorialService.updateAdminSetting(payload));
        setMessage(doneMessage);
      } catch {
        setError('保存できませんでした。もう一度お試しください。');
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  const togglePage = (pageKey: string, enabled: boolean) => {
    if (!data) return;
    const disabled = new Set(data.disabled_pages);
    if (enabled) disabled.delete(pageKey);
    else disabled.add(pageKey);
    const label = TUTORIAL_PAGES.find((page) => page.key === pageKey)?.label ?? pageKey;
    save({ disabled_pages: Array.from(disabled) }, `「${label}」のチュートリアルを${enabled ? '表示' : '非表示'}にしました。`);
  };

  const resetViews = async (pageKey?: string) => {
    const label = pageKey ? TUTORIAL_PAGES.find((page) => page.key === pageKey)?.label ?? pageKey : '全ページ';
    const ok = await confirm({
      title: `${label}の既読をリセットしますか？`,
      description: '見終えた全ユーザーに、次にページを開いたとき、もう一度チュートリアルが表示されます。',
      tone: 'destructive',
      confirmText: 'リセットする',
    });
    if (!ok) return;

    setIsSaving(true);
    setMessage(null);
    setError(null);
    try {
      const next = await tutorialService.resetViews(pageKey);
      setData(next);
      setMessage(`${label}の既読をリセットしました（${next.deleted ?? 0}件）。`);
    } catch {
      setError('既読をリセットできませんでした。');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-600">チュートリアル設定を読み込み中...</span>
      </div>
    );
  }

  const globalEnabled = data?.enabled ?? false;

  return (
    <section className="space-y-6">
      {message && <TaskyAlert tone="success">{message}</TaskyAlert>}
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-dark">チュートリアル</h2>
          <p className="mt-2 text-sm text-gray-600">
            各ページを初めて開いたユーザーに、画面を暗くして操作箇所を順に説明します。
            ユーザーが「スキップ」または最後まで見ると、そのページでは二度と表示されません。
          </p>
          <p className="mt-1 text-xs text-gray-500">変更は最大60秒で利用者の画面に反映されます。</p>
        </div>
        <TaskyButton type="button" variant="outline" size="sm" onClick={() => resetViews()} disabled={isSaving || !data}>
          全ページの既読をリセット
        </TaskyButton>
      </div>

      <div className="rounded-tasky-md border border-warm-border bg-white p-4 sm:p-5">
        <TaskySwitch
          checked={globalEnabled}
          disabled={isSaving || !data}
          onChange={(event) =>
            save({ enabled: event.target.checked }, `チュートリアルを全体で${event.target.checked ? 'ON' : 'OFF'}にしました。`)
          }
          label={<span className="font-medium">チュートリアルを表示する（全体）</span>}
        />
        {!globalEnabled && (
          <p className="mt-2 text-xs text-gray-600">全体がOFFの間は、ページ別の設定に関係なく表示されません。</p>
        )}
      </div>

      <div className="overflow-x-auto">
        <TaskyTable>
          <TaskyTableHead>
            <TaskyTableRow>
              <TaskyTableHeaderCell>ページ</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>URL</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>手順数</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>見終えた人数</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>表示</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>既読</TaskyTableHeaderCell>
            </TaskyTableRow>
          </TaskyTableHead>
          <TaskyTableBody>
            {TUTORIAL_PAGES.map((page) => {
              const pageEnabled = !(data?.disabled_pages ?? []).includes(page.key);
              return (
                <TaskyTableRow key={page.key}>
                  <TaskyTableCell>
                    <div className="font-medium text-dark">{page.label}</div>
                    <div className="text-xs text-gray-500">{page.key}</div>
                  </TaskyTableCell>
                  <TaskyTableCell>
                    <div className="space-y-0.5 text-xs text-gray-600">
                      {page.paths.map((path) => (
                        <div key={path}>{path}</div>
                      ))}
                    </div>
                  </TaskyTableCell>
                  <TaskyTableCell>{page.steps.length}</TaskyTableCell>
                  <TaskyTableCell>{data?.view_counts[page.key] ?? 0}</TaskyTableCell>
                  <TaskyTableCell>
                    <div className="flex items-center gap-2">
                      <TaskySwitch
                        aria-label={`${page.label}のチュートリアルを表示する`}
                        checked={pageEnabled}
                        disabled={isSaving || !data}
                        onChange={(event) => togglePage(page.key, event.target.checked)}
                      />
                      {pageEnabled && !globalEnabled && <TaskyBadge variant="warning">全体OFF</TaskyBadge>}
                    </div>
                  </TaskyTableCell>
                  <TaskyTableCell>
                    <TaskyButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => resetViews(page.key)}
                      disabled={isSaving || !data}
                    >
                      リセット
                    </TaskyButton>
                  </TaskyTableCell>
                </TaskyTableRow>
              );
            })}
          </TaskyTableBody>
        </TaskyTable>
      </div>
    </section>
  );
};

export default TutorialSettingsPanel;
